import type { ReportsMessages } from "./messages.en";

type Catalog = Partial<Record<keyof ReportsMessages, string>>;

export const reportsNavEs = {
  "reports.nav.dashboard": "Panel",
  "reports.nav.library": "Biblioteca de informes",
  "reports.nav.builder": "Creador de informes",
} as const;

/** Spanish for the reports screens. Anything missing falls back to English. */
export const reportsEs: Catalog = {
  "reports.contact.followup": "Seguimiento",
  "reports.contact.review": "Revisar socios",
  "reports.contact.reviewCohort": "Revisar socios: {cohort}",
  ...reportsNavEs,
  "reports.back.library": "Biblioteca de informes",

  "reports.dashboard.title": "Panel de informes",
  "reports.dashboard.subtitle": "Cifras clave, tendencias y listas destacadas con los datos del club.",
  "reports.dashboard.asOf": "Cifras al {date}. Cada cifra clave se compara con los mismos días del año pasado.",
  "reports.dashboard.noAccess": "Sin acceso. Su rol no puede ver los datos de esta sección.",
  "reports.dashboard.noData": "Aún no hay nada que mostrar para este periodo.",
  "reports.dashboard.tileError": "No se pudo calcular esta cifra.",
  "reports.dashboard.panelError": "No se pudo cargar esta sección.",
  "reports.dashboard.viewReport": "Ver informe",
  "reports.tile.up": "Sube {percent}",
  "reports.tile.down": "Baja {percent}",
  "reports.tile.same": "Sin cambios",
  "reports.tile.vs": "frente a {before} el {date}",
  "reports.tile.secondary.active_members_one": "{formatted} hogar",
  "reports.tile.secondary.active_members_other": "{formatted} hogares",
  "reports.tile.secondary.rounds_mtd_one": "{formatted} ronda de invitados",
  "reports.tile.secondary.rounds_mtd_other": "{formatted} rondas de invitados",
  "reports.tile.secondary.ar_over_60_one": "{formatted} cuenta",
  "reports.tile.secondary.ar_over_60_other": "{formatted} cuentas",

  "reports.library.title": "Biblioteca de informes",
  "reports.library.newReport": "Nuevo informe",
  "reports.library.canned": "Informes predefinidos",
  "reports.library.saved": "Informes guardados",
  "reports.library.search": "Buscar informes guardados",
  "reports.library.mine": "Solo mis informes",
  "reports.library.col.name": "Informe",
  "reports.library.col.owner": "Propietario",
  "reports.library.col.dataset": "Datos",
  "reports.library.col.sharedWith": "Compartido con",
  "reports.library.col.updated": "Actualizado",
  "reports.library.notShared": "No compartido",
  "reports.library.you": "Usted",
  "reports.library.open": "Abrir",

  "reports.canned.save": "Guardar como informe",
  "reports.canned.customize": "Personalizar en el creador",
  "reports.params.run": "Ejecutar informe",
  "reports.params.any": "Cualquiera",
  "reports.saved.duplicate": "Duplicar",
  "reports.saved.edit": "Editar en el creador",
  "reports.saved.share": "Compartir",
  "reports.saved.schedules": "Programaciones",
  "reports.delete.title": "¿Eliminar este informe?",
  "reports.share.title": "Compartir informe",
  "reports.share.roles": "Compartir con estos roles",
  "reports.save.name": "Nombre",
  "reports.save.description": "Descripción",

  "reports.schedule.title": "Programaciones de {name}",
  "reports.schedule.notLiveTitle": "El envío por correo aún no está activado.",
  "reports.schedule.notLive": "Las programaciones se guardan ahora, pero no se envía nada hasta que se active el servicio de notificaciones del club. Mientras tanto, cada programación muestra No enviado.",
  "reports.schedule.notDelivered": "No enviado",
  "reports.schedule.daily": "Cada día a las {time}",
  "reports.schedule.weekly": "Cada {weekday} a las {time}",
  "reports.schedule.monthly": "El día {day} de cada mes a las {time}",

  "reports.result.rows_one": "{formatted} fila",
  "reports.result.rows_other": "{formatted} filas",
  "reports.result.emptyNoHistoryTitle": "Aún no hay datos",
  "reports.notices.label": "Qué datos incluye",
  "reports.export.button": "Exportar CSV",
  "reports.print.button": "Imprimir",
  "reports.chart.showTable": "Ver como tabla",
  "reports.chart.showChart": "Ver gráfico",
  "reports.chart.other": "Otros",
  "reports.chartType.table": "Tabla",
  "reports.chartType.bar": "Gráfico de barras",
  "reports.chartType.line": "Gráfico de líneas",
  "reports.chartType.pie": "Gráfico circular",
  "reports.chartType.donut": "Gráfico de anillo",
  "reports.chartType.area": "Gráfico de áreas",
  "reports.chartType.kpi": "Cifra única",
  "reports.chartKind.table": "Tabla",
  "reports.chartKind.bar": "Gráfico de barras verticales",
  "reports.chartKind.barSideways": "Gráfico de barras horizontales",
  "reports.chartKind.stacked": "Gráfico de barras apiladas",
  "reports.chartKind.line": "Gráfico de líneas",
  "reports.chartKind.area": "Gráfico de áreas",
  "reports.chartKind.pie": "Gráfico circular",
  "reports.chartKind.donut": "Gráfico de anillo",
  "reports.chartKind.kpi": "Cifra única",
  "reports.chart.summaryAxis": "Más alto: {top} ({topValue}). Más bajo: {bottom} ({bottomValue}).",
  "reports.chart.summaryOne": "Un solo valor: {top} ({topValue}).",
  "reports.chart.summaryShare": "Parte mayor: {top}, {topValue} ({percent}% del total). Parte menor: {bottom}, {bottomValue}.",
  "reports.chart.summaryNone": "No hay nada que dibujar: el resultado no tiene valores.",
  "reports.chart.kpiValue": "{label}: {value}",
  "reports.builder.chartHint": "Elija cómo dibujar el resumen. La tabla se muestra siempre, y puede cambiar cualquier gráfico a su tabla.",
  "reports.builder.chartStackedAreas": "Apilar las áreas",
  "reports.builder.dragHandle": "Reordenar {name}: arrástrelo o pulse la flecha hacia arriba o hacia abajo",
  "reports.builder.groupNone": "Aún no hay agrupación, así que el informe es una sola fila de totales. Añada una agrupación, por ejemplo Departamento, para obtener una fila por cada valor.",
  "reports.builder.measureNone": "Añada una medida: qué sumar o contar en cada grupo.",
  "reports.builder.previewRejected": "No se aceptó el informe: {detail}",
  "reports.problem.chartNeedsSummary": "Un gráfico dibuja un resumen. Cambie primero la forma a Resumir.",
  "reports.problem.chartNeedsAxis": "Este gráfico necesita al menos una agrupación (a lo largo del gráfico) y una medida (los valores).",
  "reports.problem.chartShare": "Un gráfico circular o de anillo muestra las partes de un todo: use exactamente una agrupación y una medida.",
  "reports.problem.chartOrdered": "Un gráfico de líneas o de áreas necesita un eje ordenado. Agrupe por una fecha (por ejemplo por mes) o por un número.",
  "reports.problem.chartStack": "Para apilar hace falta más de una medida, o una segunda agrupación con la que dividir cada barra.",
  "reports.problem.chartSingle": "Una cifra única necesita exactamente una medida y ninguna agrupación.",
  "apiError.report_timeout": "El informe tardó demasiado y se detuvo. Acote las fechas, añada un filtro o agrupe con menos detalle.",
  "apiError.mixed_report": "Un informe o enumera filas o las resume, no ambas cosas. Quite las columnas o cambie la forma.",
  "apiError.columns_required": "Elija al menos una columna, o añada una agrupación o una medida.",
  "apiError.summary_only": "Las condiciones sobre totales y la clasificación dentro de un grupo solo valen para un resumen.",
  "apiError.cannot_group": "Ese campo no se puede usar para agrupar un informe.",
  "apiError.invalid_measure": "Ese cálculo no está disponible para este campo.",
  "apiError.invalid_bucket": "Solo las fechas se pueden agrupar por día, semana, mes, trimestre o año.",
  "apiError.duplicate_column": "La misma agrupación o medida aparece dos veces en el informe.",
  "apiError.unknown_column": "Un orden o una condición sobre totales usa una columna que no está en el informe.",
  "apiError.invalid_rank": "La clasificación dentro de un grupo necesita una de las agrupaciones del informe y un número de los primeros.",
  "apiError.invalid_value": "Un valor de este informe es demasiado grande, demasiado pequeño o no es válido para los datos.",
  "apiError.invalid_filter_op": "Esa condición no se puede usar con este campo.",
  "apiError.invalid_chart": "El gráfico usa una columna que no está en el informe.",
  "apiError.chart_needs_summary": "Un gráfico dibuja un resumen. Cambie primero la forma a Resumir.",
  "apiError.chart_needs_axis": "Este gráfico necesita al menos una agrupación (a lo largo del gráfico) y una medida (los valores).",
  "apiError.chart_share": "Un gráfico circular o de anillo muestra las partes de un todo: use exactamente una agrupación y una medida.",
  "apiError.chart_ordered_axis": "Un gráfico de líneas o de áreas necesita un eje ordenado. Agrupe por una fecha (por ejemplo por mes) o por un número.",
  "apiError.chart_stack_needs_series": "Para apilar hace falta más de una medida, o una segunda agrupación con la que dividir cada barra.",
  "apiError.chart_single_number": "Una cifra única necesita exactamente una medida y ninguna agrupación.",
  "apiError.chart_horizontal_bar_only": "Solo los gráficos de barras se pueden disponer en horizontal.",

  "reports.builder.title": "Creador de informes",
  "reports.builder.dataset": "1. Datos",
  "reports.builder.datasetLabel": "Conjunto de datos",
  "reports.builder.chooseDataset": "Elija un conjunto de datos",
  "reports.builder.shape": "2. Forma",
  "reports.mode.detail": "Listar filas",
  "reports.mode.summary": "Resumir",
  "reports.builder.columns": "3. Columnas",
  "reports.builder.groupBy": "3. Agrupar por",
  "reports.builder.measures": "4. Medidas",
  "reports.builder.filters": "Filtros",
  "reports.builder.sort": "Orden",
  "reports.builder.chart": "Gráfico",
  "reports.builder.preview": "Vista previa",
  "reports.builder.save": "Guardar",
  "reports.builder.saveButton": "Guardar informe",
  "reports.builder.saveAsNew": "Guardar como nuevo",
  "reports.builder.addFilter": "Añadir un filtro",
  "reports.builder.addGroup": "Añadir una agrupación",
  "reports.builder.addMeasure": "Añadir una medida",
  "reports.builder.addSort": "Añadir un orden",

  "reports.op.eq": "es",
  "reports.op.ne": "no es",
  "reports.op.in": "es uno de",
  "reports.op.not_in": "no es ninguno de",
  "reports.op.gt": "es mayor que",
  "reports.op.gte": "es al menos",
  "reports.op.lt": "es menor que",
  "reports.op.lte": "es como máximo",
  "reports.op.between": "está entre",
  "reports.op.contains": "contiene",
  "reports.op.starts_with": "empieza por",
  "reports.op.is_null": "está vacío",
  "reports.op.is_not_null": "no está vacío",
  "reports.op.preset": "está en",
  "reports.preset.today": "Hoy",
  "reports.preset.yesterday": "Ayer",
  "reports.preset.this_week": "Esta semana",
  "reports.preset.last_week": "La semana pasada",
  "reports.preset.this_month": "Este mes",
  "reports.preset.last_month": "El mes pasado",
  "reports.preset.this_quarter": "Este trimestre",
  "reports.preset.last_quarter": "El trimestre pasado",
  "reports.preset.this_year": "Este año",
  "reports.preset.last_year": "El año pasado",
  "reports.preset.last_7_days": "Últimos 7 días",
  "reports.preset.last_30_days": "Últimos 30 días",
  "reports.preset.last_90_days": "Últimos 90 días",
  "reports.preset.last_12_months": "Últimos 12 meses",
  "reports.preset.year_to_date": "Lo que va del año",
  "reports.agg.sum": "Total",
  "reports.agg.count": "Recuento",
  "reports.agg.avg": "Promedio",
  "reports.agg.min": "Mínimo",
  "reports.agg.max": "Máximo",
  "reports.bucket.day": "Día",
  "reports.bucket.week": "Semana",
  "reports.bucket.month": "Mes",
  "reports.bucket.quarter": "Trimestre",
  "reports.bucket.year": "Año",
};

/**
 * Spanish for the names the server sends in English: datasets, fields, canned reports, parameters, dashboard figures. An entry is named
 * `reports.dataset.<key>`, `reports.field.<dataset>.<key>`, `reports.template.<key>`, `reports.param.<name>`, `reports.tile.<key>`,
 * `reports.panel.<key>`, `reports.group.<group>` or `reports.category.<category>`; a name without an entry shows the server's own,
 * so a new dataset works in every language before anyone translates it.
 */
export const reportsNamesEs: Record<string, string> = {
  "reports.group.members": "Socios",
  "reports.group.money": "Dinero",
  "reports.group.golf": "Golf",
  "reports.group.other": "Otros",
  "reports.category.money": "Dinero",
  "reports.category.golf": "Golf",
  "reports.category.members": "Socios",
  "reports.dataset.members": "Socios",
  "reports.dataset.membership_events": "Altas y bajas de socios",
  "reports.dataset.spending": "Consumo",
  "reports.dataset.postings": "Asientos del libro",
  "reports.dataset.payments": "Pagos",
  "reports.dataset.statements": "Estados de cuenta",
  "reports.dataset.ar_aging": "Antigüedad de saldos",
  "reports.dataset.minimum_shortfalls": "Faltantes de mínimo",
  "reports.dataset.minimum_progress": "Avance del mínimo",
  "reports.dataset.tee_rounds": "Rondas jugadas",
  "reports.dataset.tee_bookings": "Reservas de salida",
  "reports.template.top_spenders": "Mayores consumos",
  "reports.template.top_spenders_by_department": "Mayores consumos por departamento",
  "reports.template.most_rounds": "Más rondas jugadas",
  "reports.template.spend_trend": "Consumo por mes y departamento",
  "reports.template.members_by_status_category": "Socios por estado y categoría",
  "reports.template.member_contact_coverage": "Cobertura de contacto de socios",
  "reports.template.member_contact_followup": "Seguimiento de contactos de socios",
  "reports.templateDescription.member_contact_coverage": "Socios por presencia de correo y teléfono principales y ciclo de vida. Solo indica presencia; no verifica validez, preferencias ni contactos adicionales. Los hogares se cuentan por fila y pueden aparecer en varias. Revise una fila para ver sus socios.",
  "reports.templateDescription.member_contact_followup": "Lista de socios sin correo o teléfono principal, con cuenta, categoría y datos de contacto existentes. Incluye todos los ciclos de vida salvo que se filtren. Solo indica presencia; no verifica validez, preferencias ni contactos adicionales.",
  "reports.field.members.contact_coverage": "Cobertura de contacto principal",
  "reports.param.coverage": "Cobertura de contacto",
  "reports.param.lifecycle": "Ciclo de vida",
  "reports.template.membership_changes": "Nuevos socios y renuncias",
  "reports.template.minimum_shortfalls": "Faltantes de mínimo",
  "reports.template.over_credit_limit": "Cuentas sobre el límite de crédito",
  "reports.template.ar_overdue": "Saldos con más de 60 o 90 días",
  "reports.tile.active_members": "Socios activos",
  "reports.tile.spend_mtd": "Consumo este mes",
  "reports.tile.rounds_mtd": "Rondas este mes",
  "reports.tile.ar_over_60": "Saldos con más de 60 días",
  "reports.tile.ar_over_90": "Saldos con más de 90 días",
  "reports.panel.spend_trend": "Consumo por mes y departamento",
  "reports.panel.rounds_trend": "Rondas jugadas por mes",
  "reports.panel.members_by_category": "Socios activos por categoría",
  "reports.panel.membership_changes": "Nuevos socios y renuncias",
  "reports.panel.top_spenders": "Mayores consumos este año",
  "reports.panel.departments": "Consumo por departamento este año",
  "reports.panel.most_rounds": "Más rondas este año",
  "reports.param.date_from": "Desde",
  "reports.param.date_to": "Hasta",
  "reports.param.top_n": "Cuántos",
  "reports.param.department": "Departamento",
  "reports.param.status": "Estado",
  "reports.param.course": "Campo",
  "reports.param.as_of": "Al",
  "reports.field.spending.posted_on": "Fecha",
  "reports.field.spending.department": "Departamento",
  "reports.field.spending.item": "Artículo",
  "reports.field.spending.member": "Socio",
  "reports.field.spending.household": "Hogar",
  "reports.field.spending.household_account": "Cuenta",
  "reports.field.spending.spend": "Consumo",
  "reports.field.members.member_number": "Número de socio",
  "reports.field.members.name": "Nombre",
  "reports.field.members.status_label": "Estado",
  "reports.field.members.category": "Categoría",
  "reports.field.members.joined_on": "Fecha de alta",
  "reports.field.tee_rounds.play_date": "Fecha de juego",
  "reports.field.tee_rounds.course": "Campo",
  "reports.field.tee_rounds.member": "Socio",
  "reports.field.tee_rounds.attributed_member": "Socio",
};
